<script lang="ts" setup>
import type { Location, LocationArea, LocationAreaGroupedByArea, LocationAreaGroupedByZip, LocationCity, LocationIds, LocationRegion } from '@/models/Location';
import { groupAreasByArea, groupAreasByZip, isArea, isAreaGroupedByArea, isAreaGroupedByZip, isCity } from '~/models/Location';
import { useLocationIdsAddItem, useLocationIdsDeleteItem, useLocationIdsReset, useLocationIdsUpdate } from '~/utils/location-ids';
import randomString from '@/composables/use-random-string';

const emit = defineEmits<{
  (event: 'update:value', ids: LocationIds): void;
  (event: 'reset', ids: LocationIds): void;
  (event: 'add', item: { type: 'regions' | 'cities' | 'areas'; value: number }): void;
  (event: 'delete', item: { type: 'regions' | 'cities' | 'areas'; value: number }): void;
}>();

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    id?: string;
    locations?: LocationRegion[];
    value?: LocationIds;
    default?: LocationIds;
    title?: string | null;
    groupByZip?: boolean;
    showCount?: boolean;
    query?: string;
  }>(),
  {
    disabled: false,
    id: () => randomString(),
    locations: () => [],
    value: () => ({ regions: [], cities: [], areas: [] }),
    default: () => ({ regions: [], cities: [], areas: [] }),
    title: null,
    groupByZip: false,
    subListsExpanded: false,
    showCount: true,
    query: '',
  },
);

const locationStore = useLocationStore();
const cityDropdownState = ref<number | null>(null);

const locations = computed(() => {
  return props.locations.map((region) => ({
    ...region,
    cities: region.cities.map((city) => ({
      ...city,
      areas: props.groupByZip ? groupAreasByZip(city.areas as LocationArea[]) : groupAreasByArea(city.areas as LocationArea[]),
    })),
  }));
});
const cities = computed(() => locations.value.map((o) => o.cities).flat());
const update = useLocationIdsUpdate(emit, props);
const reset = useLocationIdsReset(emit, props, update);
const add = useLocationIdsAddItem(emit, props, update);
const remove = useLocationIdsDeleteItem(emit, props, update);

const isCityDuplicate = (city: LocationCity) => cities.value.filter((o) => o.name === city.name).length > 1;

const getState = (city: LocationCity, area?: LocationArea | LocationAreaGroupedByZip | LocationAreaGroupedByArea) => {
  if (props.value.regions.includes(city.regionId)) return true;
  if (props.value.cities.includes(city.id)) return true;
  if (!area) {
    const areaIds = city.areas
      .map((o) => {
        if (isAreaGroupedByArea(o)) return o.zips;
        if (isAreaGroupedByZip(o)) return o.areas;
        return o;
      })
      .flat()
      .map((o) => o.id);
    if (areaIds.filter((id) => props.value.areas.includes(id)).length > 0) return null;
    return false;
  }
  if (isArea(area) && props.value.areas.includes(area.id)) return true;
  if (isAreaGroupedByArea(area)) {
    const count = area.zips.filter((o) => props.value.areas.includes(o.id)).length;
    if (count === area.zipCount) return true;
    if (count > 0) return null;
  } else if (isAreaGroupedByZip(area)) {
    const count = area.areas.filter((o) => props.value.areas.includes(o.id)).length;
    if (count === area.areaCount) return true;
    if (count > 0) return null;
  }
  return false;
};

const toggleTab = (city: LocationCity) => (cityDropdownState.value = cityDropdownState.value !== city.id ? city.id : null);

function checkLocations(...locations: Location[]): void {
  let ids = { ...props.value };
  locations.forEach((location) => {
    if (isCity(location)) ids = locationStore.checkCityIn(ids, location);
    else if (isArea(location)) ids = locationStore.checkAreasIn(ids, location);
    else if (isAreaGroupedByArea(location)) ids = locationStore.checkAreasIn(ids, ...location.zips);
    else if (isAreaGroupedByZip(location)) ids = locationStore.checkAreasIn(ids, ...location.areas);
  });
  update(ids);
}
function uncheckLocations(...locations: Location[]): void {
  let ids = { ...props.value };
  locations.forEach((location) => {
    if (isCity(location)) ids = locationStore.uncheckCityIn(ids, location);
    else if (isArea(location)) ids = locationStore.uncheckAreasIn(ids, location);
    else if (isAreaGroupedByArea(location)) ids = locationStore.uncheckAreasIn(ids, ...location.zips);
    else if (isAreaGroupedByZip(location)) ids = locationStore.uncheckAreasIn(ids, ...location.areas);
  });
  update(ids);
}

// has the same indeces as cities, so you can loop over cities and use the loop-index to get the count of checked areas
const countOfCheckedAreasPerCity = computed(() => cities.value.map((city) => countCheckedCityAreas(city)));

function countCheckedCityAreas(city: LocationCity): number {
  return props.value?.cities.includes(city.id)
    ? // if wholy city is selected, return count of all city areas
      city.areas.length
    : // else count checked city areas
      city.areas
        .map((area): number => {
          const areas = isAreaGroupedByArea(area) ? area.zips : isAreaGroupedByZip(area) ? area.areas : [area];
          return areas.filter((o) => props.value?.areas.includes(o.id)).length > 0 ? 1 : 0;
        })
        .reduce((p, c) => p + c, 0);
}

defineExpose({ value: props.value, update, reset, add, remove });

watchEffect(() => {
  if (props.query !== '') {
    const city = cities.value.find((o) => o.name.toLowerCase().startsWith(props.query.toLowerCase()));
    if (city) cityDropdownState.value = city.id;
  }
});
</script>

<template>
  <div class="tw-checkbox-list filter--locations">
    <div class="tw-collapse filter--cities">
      <template v-for="(city, cityIdx) in cities" :key="`TwFilterLocations_City_${city.id}`">
        <div class="tw-collapse--tab-container">
          <tw-ui-checkbox variant="square" :state="getState(city)" @check="checkLocations(city)" @uncheck="uncheckLocations(city)">
            <span class="label">
              {{ city.name }}
              <template v-if="isCityDuplicate(city)">- {{ locationStore.getRegionById(city.regionId)?.region2 }}</template>
            </span>
            <span v-if="showCount && countOfCheckedAreasPerCity[cityIdx] > 0" class="count">
              ({{ $t('common.phrases.x_of_y', [countOfCheckedAreasPerCity[cityIdx], city.areas.length]) }})
            </span>
          </tw-ui-checkbox>
          <tw-ui-button
            v-if="city.areaCount > 1 && city.areas.length > 0"
            :id="`TwFilterLocations_City_${city.id}_Tab`"
            :aria-controls="`TwFilterLocations_City_${city.id}_TabPanel`"
            :aria-selected="cityDropdownState === city.id"
            class="tw-collapse--tab tw-box-variant--circle"
            role="tab"
            @click="toggleTab(city)"
          >
            <Icon name="bi:chevron-down" />
          </tw-ui-button>
        </div>
        <div
          v-if="cityDropdownState === city.id"
          :id="`TwFilterLocations_City_${city.id}_TabPanel`"
          :aria-labelledby="`TwFilterLocations_City_${city.id}_Tab`"
          class="tw-collapse--tabpanel"
          role="tabpanel"
        >
          <div class="tw-collapse filter--areas">
            <template v-for="(area, areaIdx) in city.areas" :key="`TwFilterLocations_City_${cityIdx}_Area_${areaIdx}`">
              <div class="tw-collapse--tab-container">
                <tw-ui-checkbox variant="square" :state="getState(city, area)" @check="checkLocations(area)" @uncheck="uncheckLocations(area)">
                  <template v-if="isAreaGroupedByArea(area)">
                    {{ area.name || city.name }}
                  </template>
                  <template v-else-if="isAreaGroupedByZip(area)">
                    {{ area.zip }}
                  </template>
                  <template v-else-if="props.groupByZip">
                    {{ area.zip }}
                    <template v-if="area.name">({{ area.name }})</template>
                  </template>
                  <template v-else>{{ area.name || city.name }}</template>
                </tw-ui-checkbox>
              </div>
            </template>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/tw-variables.scss';

.tw-checkbox-list {
  display: flex;
  flex-flow: column nowrap;
  gap: $tw-component--gap;
  overflow: auto;
}

.tw-collapse {
}

.tw-collapse--tab {
  border: none;
  padding: 0;
}

.tw-collapse--tab-container {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
}

.tw-collapse--tabpanel {
  padding-left: 1em;
}

.tw-checkbox {
  flex: 1;
  align-items: center;
  cursor: pointer;
}

.tw-button {
  flex: 0;
  height: 1.75em;
  padding: $tw-component--gap;
}

.filter--grouped-areas,
.filter--grouped-zips {
  display: flex;
  flex-flow: column nowrap;
}

.count {
  color: $gray;
  font-size: 0.9em;
  margin-left: 0.25em;
}
</style>
